import { Box, Flex, Text, Heading, HStack, IconButton, Container, toaster } from "@libs/design";
import { Database, Bell, RefreshCw, Settings } from "@assets/icons";
import { StorageService } from "@libs/storage-service";
import { useNavigate } from "@libs/router";

import { usePostLogout } from "../../api";

import { LogoutPopover } from "./logout-popover";

function formatLastUpdated(ts: number | null): string {
  if (ts == null) return "—";
  return new Date(ts).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export const Header = () => {
  const navigate = useNavigate();
  const { mutate: logout, isLoading } = usePostLogout({
    onSuccess: () => {
      StorageService.clear();
      navigate("/auth");
      toaster.create({
        description: "Logged out successfully",
        type: "success",
      });
    },
    onError: () => {
      // Even if API fails, logout locally
      StorageService.clear();
      navigate("/auth");

      toaster.create({
        description: "Logged out with errors",
        type: "error",
      });
    },
  });

  const handleLogout = () => {
    logout();
  };

  return (
    <Box position="sticky" top={0} zIndex={50} bg="green.500" boxShadow="sm">
      <Container maxW="full" px={{ base: 3, md: 6 }} py={3}>
        {/* Top Row */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          {/* Left Section */}
          <HStack gap={3}>
            <Box
              w={{ base: 10, md: 12 }}
              h={{ base: 10, md: 12 }}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="green.600"
            >
              <IconButton
                bg="rgba(255, 255, 255, 0.3)"
                borderRadius="md"
                boxSize={{ base: 10, md: 12 }}
                aria-label="Database"
              >
                <Database size={20} />
              </IconButton>
            </Box>

            <Box>
              <Heading size={{ base: "lg", md: "xl" }} color="white" fontWeight="semibold">
                Avalon
              </Heading>
              <Text fontSize="xs" color="white" display={{ base: "none", md: "block" }}>
                Data Quality Platform
              </Text>
            </Box>
          </HStack>

          {/* Right Section */}
          <HStack gap={{ base: 2, md: 3 }}>
            {/* Notification */}
            <Box position="relative">
              <IconButton
                aria-label="Notifications"
                variant="solid"
                color="white"
                bg="rgba(255, 255, 255, 0.2)"
                _hover={{ bg: "green.600" }}
              >
                <Bell size={20} />
              </IconButton>
              <Box
                position="absolute"
                top={1}
                right={1}
                w={2.5}
                h={2.5}
                borderRadius="full"
                bg="status.error"
              />
            </Box>

            {/* Last updated (hide on mobile) */}
            <HStack gap={2} fontSize="sm" color="white" display={{ base: "none", lg: "flex" }}>
              <RefreshCw size={16} />
              <Text whiteSpace="nowrap">Last updated: {formatLastUpdated(null)}</Text>
            </HStack>

            <LogoutPopover
              onLogout={handleLogout}
              isLoading={isLoading}
              trigger={
                <IconButton
                  aria-label="Settings"
                  variant="solid"
                  bg="rgba(255, 255, 255, 0.2)"
                  _hover={{ bg: "green.600" }}
                >
                  <Settings size={20} />
                </IconButton>
              }
            />
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};
